import { inject, Injectable } from '@angular/core';
import { StepNavigationService } from '../../shared/services/step-navigation/step-navigation.service';
import { MatDialog } from '@angular/material/dialog';
import { ApplicationCreationRequest } from '../../shared/models/requests/application-creation-request';
import { ApplicationPreparationQuestionnaireService } from '../../shared/services/application-preparation-questionnaire/application-preparation-questionnaire.service';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { ModalData } from '../../../../shared/components/modal/models/modal-data';
import { UserService } from '../../../../core/services/user/user.service';
import { tap } from 'rxjs/operators';
import { CiviCRMContactStatusEnum } from '../../../../core/models/civi-crm/contact/civi-crm-contact-status-enum';

@Injectable({
    providedIn: 'root',
})
export class ContactLookupService {
    private readonly dialog = inject(MatDialog);
    private readonly userService = inject(UserService);
    private readonly stepNavigationService = inject(StepNavigationService);
    private readonly applicationPreparationQuestionnaireService = inject(
        ApplicationPreparationQuestionnaireService,
    );

    /**
     * Evaluates the CiviCRM contact status to determine if the status is appropriate for the Direct Entry application process.
     */
    evaluateContactStatus(
        contactId: number,
        contactStatus: CiviCRMContactStatusEnum,
    ): void {
        const modalDataForContactStatus = this.getModalDataForContactStatus(
            contactId,
            contactStatus,
        );

        // If modal data is present to be displayed, show the modal, then proceed with the
        // primary action specified in the modal data.
        if (modalDataForContactStatus) {
            this.dialog.open(ModalComponent, {
                disableClose: true,
                data: modalDataForContactStatus,
            });
        } else {
            // If there is no modal data to be displayed for the contact status, then proceed with pre-application steps
            // for the contact.
            this.prepareApplicationPreparationQuestionnaireForContact(
                contactId,
            );
        }
    }

    private prepareApplicationPreparationQuestionnaireForContact(
        contactId: number,
    ) {
        this.applicationPreparationQuestionnaireService.patchApplicationPreparationForm(
            {
                civiId: contactId,
            } as Partial<ApplicationCreationRequest>,
        );

        this.stepNavigationService.initializeNewApplicationSteps();
        this.stepNavigationService.navigateToFirstStep();
    }

    private getModalDataForContactStatus(
        contactId: number,
        contactStatus: CiviCRMContactStatusEnum,
    ): ModalData | null {
        switch (contactStatus) {
            case CiviCRMContactStatusEnum.UnlinkedWithUniquePrimaryEmail:
                return {
                    title: 'No EWA account for CiviCRM contact',
                    content:
                        'This contact in CiviCRM has no associated EWA account in Auth0. By proceeding, a new EWA Account will be created for this contact. Customer will receive an email notifying them of account creation. Customer must log in to their new account to verify their email address. This allows applications to be submitted on their behalf.',
                    primaryAction: {
                        title: 'Create & Proceed',
                        name: 'createAndProceed',
                        action: () => {
                            this.userService
                                .createFromContact({
                                    contactId: contactId,
                                })
                                .pipe(
                                    tap(() => {
                                        this.prepareApplicationPreparationQuestionnaireForContact(
                                            contactId,
                                        );
                                    }),
                                )
                                .subscribe();
                        },
                    },
                };

            case CiviCRMContactStatusEnum.UnlinkedWithNoPrimaryEmail:
                return {
                    title: 'CiviCRM contact has no primary email.',
                    content:
                        'An EWA account cannot be created for this user because the selected CiviCRM contact does not have a primary email address. The FCC requires an email address. Please contact the CiviCRM Manager to resolve.',
                    primaryAction: {
                        title: 'Ok',
                        name: 'close',
                        action: () => {},
                    },
                    isShowCancel: false,
                };

            case CiviCRMContactStatusEnum.UnlinkedWithNonUniquePrimaryEmail:
                return {
                    title: 'CiviCRM contact has no unique primary email.',
                    content:
                        'An EWA account cannot be created because the selected CiviCRM contact does not have a unique primary email address. The FCC requires a unique email address. Please contact the CiviCRM Manager to resolve.',
                    primaryAction: {
                        title: 'Ok',
                        name: 'close',
                        action: () => {},
                    },
                    isShowCancel: false,
                };

            case CiviCRMContactStatusEnum.LinkedToInactiveEwaAccount:
                return {
                    title: 'This CiviCRM Contact is linked to an inactive EWA Account.',
                    content:
                        'An EWA Account cannot be created for this user because the selected CiviCRM Contact is linked to an inactive EWA Account. Please reactivate the EWA Account to proceed.',
                    primaryAction: {
                        title: 'Ok',
                        name: 'close',
                        action: () => {},
                    },
                    isShowCancel: false,
                };

            case CiviCRMContactStatusEnum.LinkedToRejectedEwaAccount:
                return {
                    title: 'This CiviCRM Contact is linked to a rejected EWA Account.',
                    content:
                        'An EWA Account cannot be created for this user because the selected CiviCRM Contact is linked to a rejected EWA Account. Please approve the rejected EWA Account to proceed.',
                    primaryAction: {
                        title: 'Ok',
                        name: 'close',
                        action: () => {},
                    },
                    isShowCancel: false,
                };

            case CiviCRMContactStatusEnum.LinkedToPendingEwaAccount:
                return {
                    title: 'This CiviCRM Contact is linked to a pending EWA Account.',
                    content:
                        'An EWA Account cannot be created for this user because the selected CiviCRM Contact is linked to a pending EWA Account. Please approve the pending EWA Account to proceed.',
                    primaryAction: {
                        title: 'Ok',
                        name: 'close',
                        action: () => {},
                    },
                    isShowCancel: false,
                };

            default:
                return null;
        }
    }
}
